import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, FormSpy, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import {
  ASKING_PRICE_SELECT,
  DEAL_OUTCOME,
  CURRENCIES,
  STOCK_AT_VALUE,
} from 'models/DealRoom';
import { DealOwners } from './components/DealOwners';
import RadioSliderField from 'components/RadioSliderField';
import SelectField from 'components/SelectField';
import useFirstStepValues from './hooks';
import { AU_GST, NZ_GST } from '../../data';
import useHeightAnimation from 'hooks/useHeightAnimation';
import useCallbackRef from 'hooks/useCallbackRef';
import utils, {
  addDollarPreffix,
  addPercentageSuffix,
  extractValueFromMoneyString,
  extractValueFromPercentage,
  getFeeShare,
  getFeeSharePercentage,
  getShouldHasRevenueShares,
} from 'utils';
import { RevenueSharing } from './components/RevenueSharing';
import { CollapsibleSection } from 'components/CollapsibleSection';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useOperationStepValues from 'router/data/useOperationStepValues';
import {
  MAX_PERCENTAGE_TEXT,
  MAX_PERCENTAGE,
  DOLLAR,
  PERCENTRAGE,
  REQUIRED_VISIBLE_INFO,
} from 'data/global';
import AddressSearchField from 'components/AddressSearchField';
import { getFieldValidations } from 'validations/general';
import DealRoomFieldRequiredVisibleInfo from '../../../../components/DealRoomFieldRequiredVisibleInfo';
import useDealRoomFieldIsRequired from '../../../../hooks/useDealRoomFieldIsRequired';
import useMatchMedia from 'hooks/useMatchMedia';

import './FirstStep.styles.scss';
import './FirstStep.styles.responsive.scss';

type FirstStepProps = {
  className?: string;
  title?: string;
};

const FirstStep: React.FC<FirstStepProps> = (props) => {
  const { className, title } = props;

  const classes = classNames('first-step', className);

  const [contentFee, contentFeeRef] = useCallbackRef<HTMLDivElement>();
  const [contentSav, contentSavRef] = useCallbackRef<HTMLDivElement>();
  const { hasEnterprise, enterprise, isRWCMember } =
    useContext(CurrentUserContext);

  const {
    active,
    values: {
      dealOutcome,
      priceGuide,
      currency,
      stockAtValue,
      projectedSalePrice,
      successFeePercentage,
    },
  } = useFormState();
  const { change } = useForm();
  const { dealOutcomeOptions, industrySelectOptions, priceGuideOptions } =
    useOperationStepValues();

  const {
    dealLocationOptions,
    currencyOptions,
    includesSAVOptions,
    gstOptions,
    checkIfLocationIsAU,
    onLocationSearchChange,
  } = useFirstStepValues();

  const isPhablet = useMatchMedia('isPhablet');

  const isOtherOutcome = dealOutcome === DEAL_OUTCOME.OTHER;
  const isFixedPrice = priceGuide === ASKING_PRICE_SELECT.FIXED_PRICE;
  const hasStockAtValue = stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  const [isAmountFromRequired, isAmountFromDefaultRequired] =
    useDealRoomFieldIsRequired('amountFrom');
  const [isAmountToRequired, isAmountToDefaultRequired] =
    useDealRoomFieldIsRequired('amountTo');
  const [isAmountRequired, isAmountDefaultRequired] =
    useDealRoomFieldIsRequired('amount');
  const [isAverageStockValueRequired, isAverageStockValueDefaultRequired] =
    useDealRoomFieldIsRequired('averageStockValue');

  const hasRevenueShares = getShouldHasRevenueShares(
    projectedSalePrice,
    successFeePercentage,
  );

  const isFeeCollapseDefaultOpen = hasEnterprise || hasRevenueShares;

  const enterpriseName = enterprise?.brandName;

  useHeightAnimation({
    element: contentFee,
    defaultOpen: true,
  });

  useHeightAnimation({
    element: contentSav,
    defaultOpen: true,
  });

  const { t } = useTranslation();

  return (
    <div className={classes}>
      {title && <h1>{title}</h1>}

      <div className="first-step__fields">
        <Field
          name="name"
          label={t(isRWCMember ? 'address' : 'dealName')}
          component={isRWCMember ? AddressSearchField : InputField}
          maxLength={90}
          required
          validate={validations.required()}
          {...(!isRWCMember && { description: t('dealNameDescription') })}
        />

        <Field
          name="dealOutcome"
          label={t('outcome')}
          options={dealOutcomeOptions}
          component={SelectField}
          {...getFieldValidations(!isRWCMember)}
        />
      </div>

      {isOtherOutcome && (
        <div className="first-step__fields">
          <span />
          <Field
            name="otherDealOutcome"
            label={t('pleaseSpecifyOther')}
            component={InputField}
            maxLength={100}
            required
            validate={validations.required()}
          />
        </div>
      )}

      <div className="first-step__fields">
        <Field
          name="industry"
          label={t('whatIndustryIsItPart')}
          options={industrySelectOptions}
          component={SelectField}
          searchable
          renderAsPortal={isPhablet}
          {...getFieldValidations(!isRWCMember)}
        />

        <Field
          name="location"
          className="location-select"
          label={t('location')}
          component={SelectField}
          options={dealLocationOptions}
          searchable
          async
          renderAsPortal={isPhablet}
          onSearchChange={onLocationSearchChange}
          {...getFieldValidations(!isRWCMember)}
        />
      </div>

      <div className="first-step__fields">
        <Field
          name="companyName"
          label={t('yourCompanyName')}
          component={InputField}
        />
        <span />
      </div>

      <h2>{t('priceGuide')}</h2>

      <Field
        name="priceGuide"
        slideSteps={priceGuideOptions}
        component={RadioSliderField}
      />

      <div className="first-step__fields">
        {!isFixedPrice ? (
          <div className="amounts">
            <Field
              name="amountFrom"
              label={t('amountFrom')}
              labelChildren={
                <DealRoomFieldRequiredVisibleInfo
                  fieldName={`amountFrom${REQUIRED_VISIBLE_INFO}`}
                  fieldLabel={t('amountFrom')}
                  isDefaultRequired={isAmountFromDefaultRequired}
                />
              }
              placeholder={DOLLAR}
              format={addDollarPreffix}
              parse={extractValueFromMoneyString}
              component={InputField}
              {...getFieldValidations(!isRWCMember && isAmountFromRequired, [
                validations.positiveNumbersOnly(),
              ])}
            />
            <Field
              name="amountTo"
              label={t('amountTo')}
              labelChildren={
                <DealRoomFieldRequiredVisibleInfo
                  fieldName={`amountTo${REQUIRED_VISIBLE_INFO}`}
                  fieldLabel={t('amountTo')}
                  isDefaultRequired={isAmountToDefaultRequired}
                />
              }
              placeholder={DOLLAR}
              format={addDollarPreffix}
              parse={extractValueFromMoneyString}
              component={InputField}
              {...(priceGuide ===
                ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST && {
                description: t('askingPriceEoiDescription'),
              })}
              {...getFieldValidations(!isRWCMember && isAmountToRequired, [
                validations.positiveNumbersOnly(),
                validations.isGreatherOrEqualThanField('amountFrom'),
              ])}
            />
          </div>
        ) : (
          <Field
            name="amount"
            label={t('amount')}
            labelChildren={
              <DealRoomFieldRequiredVisibleInfo
                fieldName={`amount${REQUIRED_VISIBLE_INFO}`}
                fieldLabel={t('amount')}
                isDefaultRequired={isAmountDefaultRequired}
              />
            }
            placeholder={DOLLAR}
            format={addDollarPreffix}
            parse={extractValueFromMoneyString}
            component={InputField}
            {...getFieldValidations(!isRWCMember && isAmountRequired, [
              validations.greatherThanZero(t('onlyPositiveNumbersAreAllowed')),
            ])}
          />
        )}

        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            const location = values.location;
            const currency = values.currency;
            const newCurrency = checkIfLocationIsAU(location)
              ? CURRENCIES.AUD
              : CURRENCIES.NZD;

            if (location && !currency) {
              change('currency', newCurrency);
            }
          }}
        />
        <Field
          name="currency"
          className="currency"
          label={t('currency')}
          placeholder="$"
          component={SelectField}
          options={currencyOptions}
          {...getFieldValidations(!isRWCMember)}
        />
      </div>

      <h2>{t('stockAtValueSAV')}</h2>
      <Field
        name="stockAtValue"
        slideSteps={includesSAVOptions}
        component={RadioSliderField}
        {...getFieldValidations(!isRWCMember)}
      />

      <div ref={contentSavRef} className="animation-container">
        {hasStockAtValue && (
          <div className="first-step__fields">
            <Field
              name="averageStockValue"
              label={t('averageStockValue')}
              labelChildren={
                <DealRoomFieldRequiredVisibleInfo
                  fieldName={`averageStockValue${REQUIRED_VISIBLE_INFO}`}
                  fieldLabel={t('averageStockValue')}
                  isDefaultRequired={isAverageStockValueDefaultRequired}
                />
              }
              component={InputField}
              placeholder={DOLLAR}
              format={addDollarPreffix}
              parse={extractValueFromMoneyString}
              {...getFieldValidations(
                !isRWCMember && isAverageStockValueRequired,
                [
                  validations.positiveNumbersOnly(
                    t('onlyPositiveNumbersAreAllowed'),
                  ),
                ],
              )}
            />
            <span />
          </div>
        )}
      </div>

      <h2>{t('dealOwners')}</h2>
      <DealOwners />

      <CollapsibleSection
        defaultOpen={isFeeCollapseDefaultOpen}
        trigger={
          <div className="fee-structure-trigger">
            <h2>{t('feeStructure')}</h2>
            {enterpriseName && (
              <p>{t('specifyFeesForReports', { enterpriseName })}</p>
            )}
          </div>
        }
      >
        <h3>{t('successFees')}</h3>

        <div className="first-step__fields">
          <FormSpy
            subscription={{ values: true }}
            onChange={({
              values: {
                projectedSalePrice,
                successFeePercentage,
                successFeeShare,
              },
            }) => {
              if (!projectedSalePrice) {
                change('successFeeShare', null);
                change('successFeePercentage', null);
                return;
              }

              const total =
                utils.getIntegerFromDottedNumber(projectedSalePrice);

              if (active === 'successFeePercentage') {
                const share = getFeeShare(total, successFeePercentage);

                if (share !== successFeeShare)
                  change('successFeeShare', share || '');
              }

              if (active === 'successFeeShare') {
                const sharePercentage = getFeeSharePercentage(
                  total,
                  successFeeShare,
                );

                if (sharePercentage !== successFeePercentage)
                  change('successFeePercentage', sharePercentage || '');
              }

              if (active === 'projectedSalePrice') {
                if (successFeePercentage) {
                  change(
                    'successFeeShare',
                    getFeeShare(total, successFeePercentage),
                  );
                } else if (successFeeShare) {
                  change(
                    'successFeePercentage',
                    getFeeSharePercentage(total, successFeeShare),
                  );
                }
              }
            }}
          />

          <Field
            name="projectedSalePrice"
            label={t('projectedSalePrice')}
            placeholder={DOLLAR}
            component={InputField}
            format={addDollarPreffix}
            parse={extractValueFromMoneyString}
            validate={validations.positiveNumbersOnly(
              t('onlyPositiveNumbersAreAllowed'),
            )}
          />
          <Field
            name="successFeePercentage"
            label={t('successFee') + ' %'}
            placeholder={PERCENTRAGE}
            component={InputField}
            disabled={!projectedSalePrice}
            format={addPercentageSuffix}
            parse={(value) =>
              extractValueFromPercentage(value, successFeePercentage)
            }
            validate={validations.composeValidators(
              validations.greatherThanZero(t('onlyPositiveNumbersAreAllowed')),
              validations.maxNumber(
                t('maxNumber', {
                  max: MAX_PERCENTAGE_TEXT,
                }),
                MAX_PERCENTAGE,
              ),
            )}
          />
          <Field
            name="successFeeShare"
            label={t('successFee') + ' $'}
            placeholder={DOLLAR}
            format={addDollarPreffix}
            parse={extractValueFromMoneyString}
            component={InputField}
            disabled={!projectedSalePrice}
            validate={validations.positiveNumbersOnly(
              t('onlyPositiveNumbersAreAllowed'),
            )}
          />
        </div>

        <h3>{t('marketingPackage')}</h3>

        <div className="first-step__fields">
          <Field
            name="totalPackage"
            label={t('paidByVendor')}
            component={InputField}
            placeholder={DOLLAR}
            format={addDollarPreffix}
            parse={extractValueFromMoneyString}
            validate={validations.positiveNumbersOnly(
              t('onlyPositiveNumbersAreAllowed'),
            )}
          />
          <Field
            name="totalPackageGST"
            slideSteps={gstOptions}
            component={RadioSliderField}
            label={t('GST', {
              value: currency === CURRENCIES.NZD ? NZ_GST : AU_GST,
            })}
            {...getFieldValidations(!isRWCMember)}
          />
        </div>

        <div ref={contentFeeRef} className="animation-container">
          {hasRevenueShares && (
            <>
              <h3>{t('commissionSplit')}</h3>
              <RevenueSharing />
            </>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default FirstStep;
